import {
  ButtonFacetedFilter,
  FacetedFilterOption,
} from "@/components/button-faceted-filter";
import {
  StaggerContainer,
  StaggerItem,
} from "@/components/company/company-animations";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Timeline, TimelineItem } from "@/components/ui/timeline";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  JobOfferEnriched,
  Review,
  SocialNetworkActivity,
} from "@/hooks/useCompanyNewsData";
import { getPlatformBrandColor } from "@/lib/platform-brand-colors";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CompanyReviewPlatforms,
  CompanySocialNetworksPlatforms,
  CompanyDTO,
  JobOfferPlatform,
} from "@shaarp/common";
import {
  ExternalLinkIcon,
  HeartIcon,
  MessageCircleMore,
  NewspaperIcon,
  StarIcon as StarLucideIcon,
} from "lucide-react";
import { RiExternalLinkFill } from "react-icons/ri";
import { Link } from "react-router-dom";
import { AggregateOnlyEmptyState } from "./aggregate-only-empty-state";

export type ModalDomain =
  | "socials"
  | "reviews"
  | "jobs"
  | "articles"
  | "employee-reviews";

export type NewsModalSourcesProps = {
  domain: ModalDomain;
  company: CompanyDTO;
  posts: SocialNetworkActivity[];
  socialPlatformsOptions: FacetedFilterOption[];
  socialPlatformsSelected: CompanySocialNetworksPlatforms[];
  onSocialPlatformsChange: (
    platforms: CompanySocialNetworksPlatforms[],
  ) => void;
  reviews: Review[];
  reviewPlatformsOptions: FacetedFilterOption[];
  reviewPlatformsSelected: CompanyReviewPlatforms[];
  onReviewPlatformsChange: (platforms: CompanyReviewPlatforms[]) => void;
  jobOffers: JobOfferEnriched[];
  jobPlatformsOptions: FacetedFilterOption[];
  jobPlatformsSelected: JobOfferPlatform[];
  onJobPlatformsChange: (platforms: JobOfferPlatform[]) => void;
  hasGoogleAggregateRating: boolean;
};

export function NewsModalSources({
  domain,
  company,
  posts,
  socialPlatformsOptions,
  socialPlatformsSelected,
  onSocialPlatformsChange,
  reviews,
  reviewPlatformsOptions,
  reviewPlatformsSelected,
  onReviewPlatformsChange,
  jobOffers,
  jobPlatformsOptions,
  jobPlatformsSelected,
  onJobPlatformsChange,
  hasGoogleAggregateRating,
}: NewsModalSourcesProps): React.ReactElement {
  const { t } = useTranslation();

  const pressArticles = company.news?.articles ?? [];

  switch (domain) {
    case "socials":
      return (
        <div className="space-y-4">
          <ButtonFacetedFilter
            title={t("company.socialNetworks.platforms")}
            compacted={false}
            options={socialPlatformsOptions}
            selected={socialPlatformsSelected}
            onChange={(selected) =>
              onSocialPlatformsChange(
                selected as CompanySocialNetworksPlatforms[],
              )
            }
          />
          <StaggerContainer>
            <Timeline>
              {posts
                .filter((p) => socialPlatformsSelected.includes(p.platform))
                .map((post, index) => (
                  <StaggerItem key={index}>
                    <TimelineItem className="w-full">
                      <Card className="transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm">
                        <CardHeader className="flex flex-row justify-between items-center pb-2 space-y-0">
                          <CardTitle className="w-full">
                            <div className="flex flex-row gap-5">
                              <Avatar>
                                <AvatarFallback>
                                  <post.icon
                                    className={cn(
                                      "w-5 h-5",
                                      getPlatformBrandColor(post.platform),
                                    )}
                                  />
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="text-sm font-semibold">
                                  {post.author}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {post.date}
                                </div>
                              </div>
                              <div className="flex flex-col justify-center">
                                <Button size="sm" variant="outline" asChild>
                                  <Link to={post.link} target="_blank">
                                    {t("company.socialNetworks.view")}
                                    <RiExternalLinkFill className="ml-2" />
                                  </Link>
                                </Button>
                              </div>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>{post.content}</CardContent>
                        {(!!post.countComments || !!post.countLikes) && (
                          <div className="flex items-center gap-4 px-6 pb-4 pt-0">
                            {!!post.countComments && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <MessageCircleMore className="w-4 h-4 text-blue-500" />
                                    {post.countComments}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t("company.socialNetworks.commentsTooltip")}
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {!!post.countLikes && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <HeartIcon
                                      className="w-4 h-4 text-rose-500"
                                      fill="currentColor"
                                    />
                                    {post.countLikes}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t("company.socialNetworks.likesTooltip")}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </Card>
                    </TimelineItem>
                  </StaggerItem>
                ))}
            </Timeline>
          </StaggerContainer>
        </div>
      );

    case "reviews": {
      if (reviews.length === 0 && hasGoogleAggregateRating) {
        const googleHref = company.googlePlaceId
          ? `https://www.google.com/maps/place/?q=place_id:${company.googlePlaceId}`
          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.name)}`;
        return (
          <AggregateOnlyEmptyState
            caption={t("company.reviews.aggregateOnlyCaption")}
            ctaLabel={t("company.reviews.viewOnGoogle")}
            ctaHref={googleHref}
          />
        );
      }
      return (
        <div className="space-y-4">
          <ButtonFacetedFilter
            title={t("company.reviews.platforms")}
            compacted={false}
            options={reviewPlatformsOptions}
            selected={reviewPlatformsSelected}
            onChange={(selected) =>
              onReviewPlatformsChange(selected as CompanyReviewPlatforms[])
            }
          />
          <StaggerContainer>
            <Timeline>
              {reviews
                .filter((review) =>
                  reviewPlatformsSelected.includes(review.platform),
                )
                .map((review) => (
                  <StaggerItem key={review.id}>
                    <TimelineItem className="w-full">
                      <Card className="transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm">
                        <CardHeader className="flex flex-row justify-between items-center pb-2 space-y-0">
                          <CardTitle className="w-full">
                            <div className="flex flex-row gap-5">
                              <Avatar>
                                <AvatarFallback>
                                  <review.icon
                                    className={cn(
                                      "w-5 h-5",
                                      getPlatformBrandColor(review.platform),
                                    )}
                                  />
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="text-sm font-semibold">
                                  {review.author}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {review.date}
                                </div>
                              </div>
                              <div className="flex flex-col justify-center">
                                <Button size="sm" variant="outline" asChild>
                                  <Link to={review.sourceLink} target="_blank">
                                    {t("company.reviews.view")}
                                    <RiExternalLinkFill className="ml-2" />
                                  </Link>
                                </Button>
                              </div>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>{review.content}</CardContent>
                        {(!!review.likes || !!review.rating) && (
                          <div className="flex items-center gap-4 px-6 pb-4 pt-0">
                            {!!review.rating && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <StarLucideIcon
                                      className="w-4 h-4 text-amber-500"
                                      fill="currentColor"
                                    />
                                    {review.rating}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t("company.reviews.ratingTooltip")}
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {!!review.likes && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                    <HeartIcon
                                      className="w-4 h-4 text-rose-500"
                                      fill="currentColor"
                                    />
                                    {review.likes}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t("company.reviews.likesTooltip")}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </Card>
                    </TimelineItem>
                  </StaggerItem>
                ))}
            </Timeline>
          </StaggerContainer>
        </div>
      );
    }

    case "articles":
      if (pressArticles.length === 0) {
        return (
          <div className="text-sm text-muted-foreground text-center py-8">
            {t("company.news.noArticles")}
          </div>
        );
      }
      return (
        <StaggerContainer>
          <Timeline>
            {pressArticles.map((article, index) => (
              <StaggerItem key={`${article.headline}-${index}`}>
                <TimelineItem className="w-full">
                  <Card className="transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm">
                    <CardHeader className="space-y-0 pb-3">
                      <CardTitle className="flex flex-row items-start gap-4">
                        <Avatar>
                          <AvatarFallback className="bg-gradient-to-br from-amber-400 to-rose-500 text-white">
                            <NewspaperIcon className="w-4 h-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold line-clamp-2">
                            {article.headline}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                            <span>
                              {article.source ??
                                t("company.news.unknownSource")}
                            </span>
                            {article.publishedDate && (
                              <>
                                <span className="text-muted-foreground/50">
                                  ·
                                </span>
                                <span>
                                  {new Date(
                                    article.publishedDate,
                                  ).toLocaleDateString()}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {article.articleUrl && (
                          <Button
                            size="sm"
                            variant="outline"
                            asChild
                            className="shrink-0"
                          >
                            <Link to={article.articleUrl} target="_blank">
                              {t("company.news.readArticle")}
                              <ExternalLinkIcon className="ml-2 w-3.5 h-3.5" />
                            </Link>
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    {article.summary && (
                      <CardContent className="pt-0 text-sm text-muted-foreground">
                        {article.summary}
                      </CardContent>
                    )}
                  </Card>
                </TimelineItem>
              </StaggerItem>
            ))}
          </Timeline>
        </StaggerContainer>
      );

    case "jobs":
      return (
        <div className="space-y-4">
          <ButtonFacetedFilter
            title={t("company.jobOffers.platforms")}
            compacted={false}
            options={jobPlatformsOptions}
            selected={jobPlatformsSelected}
            onChange={(selected) =>
              onJobPlatformsChange(selected as JobOfferPlatform[])
            }
          />
          <StaggerContainer>
            <Timeline>
              {jobOffers
                .filter((j) => jobPlatformsSelected.includes(j.platform))
                .map((jobOffer, index) => (
                  <StaggerItem key={index}>
                    <TimelineItem className="w-full">
                      <Card className="transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm">
                        <CardHeader className="flex flex-row justify-between items-center space-y-0">
                          <CardTitle className="w-full">
                            <div className="flex flex-row gap-5">
                              <Avatar>
                                <AvatarFallback>
                                  <jobOffer.icon
                                    className={cn(
                                      "w-5 h-5",
                                      getPlatformBrandColor(jobOffer.platform),
                                    )}
                                  />
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex flex-col flex-1 gap-2">
                                <div className="font-semibold text-md">
                                  {jobOffer.title}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {jobOffer.date}
                                </div>
                              </div>
                              <div className="flex flex-col justify-center">
                                <Button size="sm" variant="outline" asChild>
                                  <Link to={jobOffer.link} target="_blank">
                                    {t("company.jobOffers.view")}
                                    <RiExternalLinkFill className="ml-2" />
                                  </Link>
                                </Button>
                              </div>
                            </div>
                          </CardTitle>
                        </CardHeader>
                      </Card>
                    </TimelineItem>
                  </StaggerItem>
                ))}
            </Timeline>
          </StaggerContainer>
        </div>
      );

    case "employee-reviews":
      return (
        <AggregateOnlyEmptyState
          caption={t("company.employeeReviews.aggregateOnlyCaption")}
          ctaLabel={t("company.employeeReviews.searchOnGlassdoor")}
          ctaHref={`https://www.glassdoor.com/Search/results.htm?keyword=${encodeURIComponent(company.name)}`}
        />
      );
  }
}
